import React from 'react';
import { TextBlock, Text } from 'bench-internal-theme';
import { Connection, ConnectionSource, LedgerAccount } from '../types';
import { useClient } from '../../ClientContext';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import LedgerTableRow from './LedgerTableRow';
import { useQuery } from '@apollo/client';
import { GET_LEDGER_ACCOUNTS } from '../queries';

type LedgerTableProps = {
  connection: Connection;
};

const LedgerTableLayout = styled.div`
  padding-top: 8px;
`;

type TableHeaderLayoutProps = {
  isStripeConnection: boolean;
};

const TableHeaderLayout = styled(TextBlock).attrs(() => ({
  color: colors.smoke,
  styleType: 'small',
  uppercase: true,
}))<TableHeaderLayoutProps>`
  align-items: center;
  background-color: ${colors.pearl};
  border-top: 1px solid ${colors.silt};
  box-shadow: 0 1px 0 ${colors.silt};
  min-height: 33px;
  display: grid;
  grid-column-gap: 16px;
  padding: 8px;
  grid-template-columns: ${({ showOpeningBalance }) =>
    showOpeningBalance ? '1fr 1fr 1fr' : '1fr 1fr'};
}
`;

const LedgerTable = ({ connection }: LedgerTableProps) => {
  const { id: clientId } = useClient();

  const { loading, error, data } = useQuery(GET_LEDGER_ACCOUNTS, {
    variables: { clientId },
  });

  if (loading) {
    return (
      <Text styleType="small" color={colors.cloud}>
        Loading...
      </Text>
    );
  }

  if (error) {
    return (
      <Text styleType="small" color={colors.cloud}>
        There was a problem fetching ledger accounts for this connection.
      </Text>
    );
  }
  const accountsForConnection = connection.accounts.map(account => account.id);
  const ledgersMappedToAccountsForConnection =
    data?.ledgerAccounts.filter(
      (ledger: LedgerAccount) =>
        ledger.accountSettings?.fdsAccountId &&
        accountsForConnection.includes(ledger.accountSettings.fdsAccountId),
    ) || [];

  const showOpeningBalance =
    connection.source === ConnectionSource[ConnectionSource.STRIPE] ||
    connection.source === ConnectionSource[ConnectionSource.PAYPAL];

  return (
    <LedgerTableLayout>
      <TableHeaderLayout showOpeningBalance={showOpeningBalance}>
        <div>Ledger Account</div>
        <div>Validated Period</div>
        {showOpeningBalance && <div>Opening Balance</div>}
      </TableHeaderLayout>
      {ledgersMappedToAccountsForConnection.length === 0 ? (
        <Text styleType="small" color={colors.cloud}>
          No mapped ledgers found for connection.
        </Text>
      ) : (
        <>
          {ledgersMappedToAccountsForConnection.map(ledger => (
            <LedgerTableRow key={ledger.id} connection={connection} ledger={ledger} />
          ))}
        </>
      )}
    </LedgerTableLayout>
  );
};

export default LedgerTable;
